import { useState, useEffect, useLayoutEffect, useRef, ReactNode, Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { usePostHog } from "@posthog/react";
import {
  RefreshCcwIcon,
  Search,
  Plus,
  Upload,
  Loader2,
  Filter,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { modulesTypes } from "@/convex/system/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { Skeleton } from "@/components/ui/skeleton";
import { DateRangePicker } from "../ui/date-range-picker";
import { NumericRangePicker } from "../ui/numeric-range-picker";
import { cn, getNumericRange, updateNumericRange } from "@/lib/utils";
import DynamicIcon from "../utils/lucide-icon";
import { useSearchLoading } from "@/hooks/useSearchLoading";
import { Badge } from "../ui/badge";
import * as Icons from "lucide-react";
import { useDebounce } from "@/hooks/utils/useDebounce";
import { useCommonI18n } from "@/hooks/useModuleI18n";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { TooltipKbd } from "@/components/ui/action-tooltip";
import {
  MultiSelectFilter,
  parseMultiSelectValue,
  serializeMultiSelectValue,
} from "./multi-select-filter";
import { filterTriggerClass } from "./filter-trigger";
import { DateRangeFilter } from "./date-range-filter";

type IconName = keyof typeof Icons;

export interface Filter {
  id: string;
  name: string;
  options?: Record<string, any>[];
  defaultValue?: string;
  /**
   * `multi_select`: varias opciones a la vez; el valor viaja en la URL como
   * valores separados por coma (ver multi-select-filter.tsx). Sus opciones
   * aceptan `keywords` (texto para el buscador si `label` es un nodo).
   */
  type?:
    | "date"
    | "date_range"
    | "numeric_range"
    | "number"
    | "string"
    | "multi_select"
    /** Marcador mientras cargan los filtros propios (p. ej. de una
     *  colección): se pinta como esqueleto del tamaño de un filtro. */
    | "loading";
  icon?: IconName;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
  decimalPlaces?: number;
  allowDecimals?: boolean;
}

export interface HeaderAction {
  label: string;
  icon?: LucideIcon;
  /**
   * Handler del botón simple (alternativa a `render`). Si se define `render`,
   * `onClick` se ignora y se usa el componente custom en su lugar.
   */
  onClick?: () => void;
  /**
   * Componente custom para acciones que necesitan UI compleja (popover,
   * dropdown, etc.). Cuando se pasa, el factory renderiza esto directamente
   * sin envolverlo en un Button ni Tooltip. El componente custom debe
   * traer su propio trigger.
   */
  render?: () => ReactNode;
  variant?: "default" | "secondary" | "outline" | "ghost" | "destructive";
  /** «start»: al inicio de la barra, antes del buscador (p. ej. el selector
   *  de vistas de una colección, en el mismo lugar que en kanban y calendario). */
  placement?: "start" | "end";
}

export const TableFilters = ({
  setIsModalOpen,
  updateFilters,
  filters: allFilters,
  table,
  tableName,
  showImportButton = false,
  onImportClick,
  rootPath,
  createRoute,
  defaultPageSize = 5,
  headerActions = [],
  createShortcut,
  renderSearch,
}: {
  setIsModalOpen?: (open: boolean) => void;
  updateFilters: (filters: Record<string, any>) => void;
  filters: Filter[];
  table: typeof modulesTypes.type;
  tableName: string;
  showImportButton?: boolean;
  onImportClick?: () => void;
  rootPath?: string;
  createRoute?: string;
  defaultPageSize?: number;
  headerActions?: HeaderAction[];
  /** Tecla del atajo para crear (p. ej. "N"); se muestra en el tooltip del
   *  botón de agregar. Sin atajo, no se muestra. */
  createShortcut?: string;
  /**
   * Buscador propio (p. ej. la lupa plegable de las vistas de colecciones):
   * reemplaza el campo de la izquierda y va primero entre los controles de
   * la derecha. Si devuelve null, queda el campo de siempre.
   */
  renderSearch?: (props: { value: string; onSearch: (value: string) => void }) => ReactNode;
}) => {
  const search = useSearch({ strict: false }) as Record<string, any>;
  const navigate = useNavigate();
  // Los marcadores de carga solo se pintan como esqueletos (escritorio).
  const loadingFilters = allFilters.filter((f) => f.type === "loading");
  const filters = allFilters.filter((f) => f.type !== "loading");
  const { can, isLoading: permissionsLoading } = usePermissions(table);
  const { isSearchLoading } = useSearchLoading();
  const { t } = useCommonI18n();
  const posthog = usePostHog();

  const [searchTerm, setSearchTerm] = useState(search.search || "");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const isMobile = useMediaQuery("(max-width: 1023px)");

  const targetDateFrom = search.targetDateFrom || "";
  const targetDateTo = search.targetDateTo || "";
  const numericRangesParam = search.numericRanges || "";
  const isFilterActive = (id: string) =>
    search[id] != null && search[id] !== "" && search[id] !== "all";
  const isRowsActive =
    search.targetMaxRows != null &&
    Number(search.targetMaxRows) !== defaultPageSize;

  const filterStorageKey = `table-filters-${table}`;
  const filterActionRef = useRef(false);

  useEffect(() => {
    const filtersToSave: Record<string, any> = {};
    for (const filter of filters) {
      const value = search[filter.id];
      if (value && value !== "all") {
        filtersToSave[filter.id] = value;
      }
    }
    if (search.numericRanges) {
      filtersToSave["numericRanges"] = search.numericRanges;
    }
    if (search.targetMaxRows != null) {
      filtersToSave["targetMaxRows"] = search.targetMaxRows;
    }
    if (Object.keys(filtersToSave).length > 0) {
      localStorage.setItem(filterStorageKey, JSON.stringify(filtersToSave));
    } else {
      localStorage.removeItem(filterStorageKey);
    }
  }, [search, filters, filterStorageKey]);

  useLayoutEffect(() => {
    if (filterActionRef.current) {
      filterActionRef.current = false;
      return;
    }

    const hasFilterParams = Object.keys(search).some(
      (k) => k !== "page" && search[k],
    );
    const hasNumericRanges = !!search.numericRanges;
    if (!hasFilterParams && !hasNumericRanges) {
      const saved = localStorage.getItem(filterStorageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const activeFilters: Record<string, any> = {};
          for (const [key, value] of Object.entries(parsed)) {
            if (value && value !== "all") {
              activeFilters[key] = value;
            }
          }
          if (Object.keys(activeFilters).length > 0) {
            navigate({
              to: rootPath || `/${table}`,
              search: (prev: any) => ({ ...prev, ...activeFilters }),
              replace: true,
            });
          }
        } catch {
          localStorage.removeItem(filterStorageKey);
        }
      }
    }
  }, [search, filterStorageKey, rootPath, table, navigate]);

  const handleSearchTermChange = (value: string) => {
    filterActionRef.current = true;
    navigate({
      to: rootPath || `/${table}`,
      search: (prev: any) => ({
        ...prev,
        search: value || undefined,
        page: 1,
      }),
    });
  };

  const debouncedSearch = useDebounce((value: string) => {
    handleSearchTermChange(value);
  }, 500);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchTerm(event.target.value);
    debouncedSearch(event.target.value);
  };

  const handleNewItem = () => {
    if (!can("create")) return;

    posthog.capture("new_item_clicked", {
      table: table,
      table_name: tableName,
    });

    if (createRoute) {
      navigate({ to: createRoute as any });
    } else if (setIsModalOpen) {
      setIsModalOpen(true);
    }
  };

  const handleNumericRangeUpdate = (
    filterId: string,
    min: number | undefined,
    max: number | undefined,
  ) => {
    filterActionRef.current = true;
    const newNumericRanges = updateNumericRange(
      numericRangesParam,
      filterId,
      min,
      max,
    );

    navigate({
      to: rootPath || `/${table}`,
      search: (prev: any) => ({
        ...prev,
        numericRanges: newNumericRanges || undefined,
        page: 1,
      }),
    });
  };

  const handleResetFilters = () => {
    filterActionRef.current = true;
    localStorage.removeItem(filterStorageKey);
    navigate({
      to: rootPath || `/${table}`,
      search: {},
    });

    setSearchTerm("");
  };

  // Buscador propio (o null: el campo de siempre, a la izquierda).
  const customSearch = renderSearch?.({
    value: searchTerm,
    onSearch: (value) => {
      setSearchTerm(value);
      handleSearchTermChange(value);
    },
  });
  const hasCustomSearch = customSearch !== null && customSearch !== undefined;
  const startActions = headerActions.filter((a) => a.placement === "start");
  const endActions = headerActions.filter((a) => a.placement !== "start");

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-2 md:gap-1 md:items-center md:justify-between">
          <div className="flex flex-col md:flex-row gap-1 md:items-center md:flex-1 lg:min-w-0">
            {/* Acciones de inicio y búsqueda con la separación de los filtros. */}
            <div className="flex items-center gap-1 w-full">
              {startActions.map((action, index) => (
                <Fragment key={`start-action-${index}`}>
                  {action.render ? (
                    action.render()
                  ) : (
                    <Button variant={action.variant ?? "outline"} size="icon" onClick={action.onClick} aria-label={action.label}>
                      {action.icon ? <action.icon /> : action.label}
                    </Button>
                  )}
                </Fragment>
              ))}
              {isMobile && customSearch}
              <div className={cn("flex-1", hasCustomSearch && "hidden")}>
                <InputGroup className="w-full md:min-w-45 md:max-w-62.5 has-[[data-slot=input-group-control]:focus-visible]:border-ring/70 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/30 has-[[data-slot=input-group-control]:focus-visible]:ring-1">
                  <InputGroupInput
                    placeholder={t("table.filters.searchPlaceholder")}
                    onChange={handleSearchInputChange}
                    value={searchTerm}
                    className="hover:shadow-none!"
                  />
                  <InputGroupAddon>
                    <Search />
                  </InputGroupAddon>
                </InputGroup>
              </div>

              {isMobile && (
              <div className="flex items-center gap-1">
                <Popover
                  open={mobileFiltersOpen}
                  onOpenChange={setMobileFiltersOpen}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                        >
                          <Filter />
                        </Button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("table.filters.title")}</p>
                    </TooltipContent>
                  </Tooltip>
                  <PopoverContent
                    className="w-80 max-h-[70vh] overflow-y-auto"
                    align="end"
                  >
                    <div className="space-y-4">
                      <div className="font-medium text-sm">
                        {t("table.filters.title")}
                      </div>

                      {filters
                        .filter(
                          (f) =>
                            f.type !== "numeric_range" &&
                            f.type !== "multi_select" &&
                            f.type !== "date_range",
                        )
                        .map((filter) => (
                          <div key={filter.id} className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">
                              {filter.name}
                            </label>
                            <Select
                              value={
                                filter.options?.find(
                                  (option) =>
                                    option.value === search[filter.id],
                                )?.value || filter.defaultValue
                              }
                              onValueChange={(value) => {
                                filterActionRef.current = true;
                                if (value === "all") {
                                  updateFilters({ [filter.id]: undefined });
                                } else {
                                  updateFilters({ [filter.id]: value });
                                }
                                setMobileFiltersOpen(false);
                              }}
                            >
                              <SelectTrigger
                                className={cn(
                                  "w-full",
                                  filterTriggerClass(isFilterActive(filter.id)),
                                )}
                              >
                                {filter.icon && (
                                  <DynamicIcon name={filter.icon} />
                                )}
                                <SelectValue placeholder={filter.name} />
                              </SelectTrigger>
                              <SelectContent>
                                {filter.options?.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}

                      {filters
                        .filter((f) => f.type === "multi_select")
                        .map((filter) => (
                          <div key={filter.id} className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">
                              {filter.name}
                            </label>
                            <MultiSelectFilter
                              filter={filter}
                              fullWidth
                              value={parseMultiSelectValue(search[filter.id])}
                              onChange={(values) => {
                                filterActionRef.current = true;
                                updateFilters({
                                  [filter.id]:
                                    serializeMultiSelectValue(values),
                                });
                              }}
                            />
                          </div>
                        ))}

                      {filters
                        .filter((f) => f.type === "date_range")
                        .map((filter) => (
                          <div key={filter.id} className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground">
                              {filter.name}
                            </label>
                            <DateRangeFilter
                              name={filter.name}
                              fullWidth
                              value={search[filter.id]}
                              onChange={(value) => {
                                filterActionRef.current = true;
                                updateFilters({ [filter.id]: value });
                                setMobileFiltersOpen(false);
                              }}
                            />
                          </div>
                        ))}

                      {filters
                        .filter((f) => f.type === "numeric_range")
                        .map((filter) => {
                          const currentRange = getNumericRange(
                            numericRangesParam,
                            filter.id,
                          );

                          return (
                            <div key={filter.id} className="space-y-2">
                              <label className="text-xs font-medium text-muted-foreground">
                                {filter.name}
                              </label>
                              <NumericRangePicker
                                label={filter.name}
                                prefix={filter.prefix || "L. "}
                                suffix={filter.suffix || ""}
                                allowDecimals={filter.allowDecimals ?? true}
                                decimalPlaces={filter.decimalPlaces ?? 2}
                                initialMin={currentRange?.min ?? filter.min}
                                initialMax={currentRange?.max ?? filter.max}
                                triggerClassName={cn(
                                  "w-full",
                                  filterTriggerClass(!!currentRange),
                                )}
                                onUpdate={({ range }) => {
                                  handleNumericRangeUpdate(
                                    filter.id,
                                    range.min,
                                    range.max,
                                  );
                                  setMobileFiltersOpen(false);
                                }}
                              />
                            </div>
                          );
                        })}

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          {t("table.filters.show")}
                        </label>
                        <Select
                          onValueChange={(value) => {
                            filterActionRef.current = true;
                            updateFilters({ targetMaxRows: Number(value) });
                            setMobileFiltersOpen(false);
                          }}
                          value={
                            search.targetMaxRows
                              ? String(search.targetMaxRows)
                              : String(defaultPageSize)
                          }
                        >
                          <SelectTrigger
                            className={cn("w-full", filterTriggerClass(isRowsActive))}
                          >
                            <SelectValue
                              placeholder={t("table.filters.show")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {[5, 10, 20, 50].map((rows) => (
                              <SelectItem key={rows} value={rows.toString()}>
                                {rows} {t("table.filters.rows")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          {t("table.filters.dateRange")}
                        </label>
                        <DateRangePicker
                          key={`${targetDateFrom}-${targetDateTo}`}
                          onUpdate={(values) => {
                            filterActionRef.current = true;
                            updateFilters({
                              targetDateFrom: values.range.from.toString(),
                              targetDateTo: values.range?.to?.toString() || "",
                            });
                            setMobileFiltersOpen(false);
                          }}
                          initialDateFrom={
                            targetDateFrom
                              ? new Date(Number(targetDateFrom))
                              : undefined
                          }
                          initialDateTo={
                            targetDateTo
                              ? new Date(Number(targetDateTo))
                              : undefined
                          }
                          align="start"
                          locale="en-GB"
                          showCompare={false}
                          triggerClassName={cn(
                            "w-full",
                            filterTriggerClass(!!targetDateFrom),
                          )}
                        />
                      </div>

                      <Button
                        variant="outline"
                        onClick={() => {
                          handleResetFilters();
                          setMobileFiltersOpen(false);
                        }}
                        className="w-full"
                      >
                        <RefreshCcwIcon className="mr-1" />
                        {t("table.filters.resetFilters")}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {endActions.map((action, index) => {
                  // Las acciones con `render` traen su propio Button + trigger,
                  // por lo que no se envuelven en Button ni Tooltip. Se renderizan
                  // en Fragment para que su Button interno sea hijo directo del
                  // flex row y respete el gap del contenedor padre.
                  if (action.render) {
                    return (
                      <div
                        key={`mobile-action-${index}`}
                        className="contents"
                      >
                        {action.render()}
                      </div>
                    );
                  }
                  const Icon = action.icon;
                  return (
                    <Tooltip key={`mobile-action-${index}`}>
                      <TooltipTrigger asChild>
                        <Button
                          variant={action.variant ?? "outline"}
                          size="icon"
                          onClick={action.onClick}
                        >
                          {Icon && <Icon />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{action.label}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}

                {showImportButton &&
                  onImportClick &&
                  (permissionsLoading ? (
                    <Skeleton className="size-8 rounded-md" />
                  ) : can("create") ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          onClick={onImportClick}
                        >
                          <Upload />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {t("table.filters.import")} {tableName}s
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : null)}

                {(setIsModalOpen || createRoute) &&
                  (permissionsLoading ? (
                    <Skeleton className="size-8 rounded-md" />
                  ) : can("create") ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          onClick={handleNewItem}
                        >
                          <Plus />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="flex items-center gap-2">
                        <p>
                          {t("table.filters.add")} {tableName}
                        </p>
                        {createShortcut && <TooltipKbd>{createShortcut}</TooltipKbd>}
                      </TooltipContent>
                    </Tooltip>
                  ) : null)}
              </div>
              )}
            </div>

            {!isMobile && (
            <div className="flex flex-row gap-1 shrink-0">
              {customSearch}
              {loadingFilters.map((filter) => (
                <Skeleton key={filter.id} className="h-control w-24 rounded-md" />
              ))}
              {filters
                .filter(
                  (f) =>
                    f.type !== "numeric_range" &&
                    f.type !== "multi_select" &&
                    f.type !== "date_range",
                )
                .map((filter) => (
                  <Tooltip key={filter.id}>
                    <TooltipTrigger asChild>
                      <div>
                        <Select
                          value={
                            filter.options?.find(
                              (option) => option.value === search[filter.id],
                            )?.value || filter.defaultValue
                          }
                          onValueChange={(value) => {
                            filterActionRef.current = true;
                            if (value === "all") {
                              updateFilters({ [filter.id]: undefined });
                            } else {
                              updateFilters({ [filter.id]: value });
                            }
                          }}
                        >
                          <SelectTrigger
                            className={cn(
                              "w-fit min-w-[70px] whitespace-nowrap",
                              filterTriggerClass(isFilterActive(filter.id)),
                            )}
                          >
                            {filter.icon && <DynamicIcon name={filter.icon} />}
                            <SelectValue placeholder={filter.name} />
                          </SelectTrigger>
                          <SelectContent>
                            {filter.options?.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{filter.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}

              {filters
                .filter((f) => f.type === "multi_select")
                .map((filter) => (
                  <MultiSelectFilter
                    key={filter.id}
                    filter={filter}
                    value={parseMultiSelectValue(search[filter.id])}
                    onChange={(values) => {
                      filterActionRef.current = true;
                      updateFilters({
                        [filter.id]: serializeMultiSelectValue(values),
                      });
                    }}
                  />
                ))}

              {filters
                .filter((f) => f.type === "date_range")
                .map((filter) => (
                  <Tooltip key={filter.id}>
                    <TooltipTrigger asChild>
                      <div>
                        <DateRangeFilter
                          name={filter.name}
                          value={search[filter.id]}
                          onChange={(value) => {
                            filterActionRef.current = true;
                            updateFilters({ [filter.id]: value });
                          }}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{filter.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}

              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select
                      onValueChange={(value) => {
                        filterActionRef.current = true;
                        updateFilters({ targetMaxRows: Number(value) });
                      }}
                      value={
                        search.targetMaxRows
                          ? String(search.targetMaxRows)
                          : String(defaultPageSize)
                      }
                    >
                      <SelectTrigger
                        className={cn(
                          "w-fit min-w-[50px] 2xl:min-w-[70px] whitespace-nowrap",
                          filterTriggerClass(isRowsActive),
                        )}
                      >
                        <SelectValue placeholder={t("table.filters.show")} />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 10, 20, 50].map((rows) => (
                          <SelectItem key={rows} value={rows.toString()}>
                            {rows}
                            <span className="hidden 2xl:inline">
                              {" "}
                              {t("table.filters.rows")}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("table.filters.showRows")}</p>
                </TooltipContent>
              </Tooltip>

              {filters
                .filter((f) => f.type === "numeric_range")
                .map((filter) => {
                  const currentRange = getNumericRange(
                    numericRangesParam,
                    filter.id,
                  );

                  return (
                    <Tooltip key={filter.id}>
                      <TooltipTrigger asChild>
                        <div>
                          <NumericRangePicker
                            label={filter.name}
                            prefix={filter.prefix || "L. "}
                            suffix={filter.suffix || ""}
                            allowDecimals={filter.allowDecimals ?? true}
                            decimalPlaces={filter.decimalPlaces ?? 2}
                            initialMin={currentRange?.min ?? filter.min}
                            initialMax={currentRange?.max ?? filter.max}
                            triggerClassName={filterTriggerClass(!!currentRange)}
                            onUpdate={({ range }) => {
                              handleNumericRangeUpdate(
                                filter.id,
                                range.min,
                                range.max,
                              );
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{filter.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}

              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <DateRangePicker
                      key={`${targetDateFrom}-${targetDateTo}`}
                      onUpdate={(values) => {
                        filterActionRef.current = true;
                        updateFilters({
                          targetDateFrom: values.range.from.toString(),
                          targetDateTo: values.range?.to?.toString() || "",
                        });
                      }}
                      initialDateFrom={
                        targetDateFrom
                          ? new Date(Number(targetDateFrom))
                          : undefined
                      }
                      initialDateTo={
                        targetDateTo
                          ? new Date(Number(targetDateTo))
                          : undefined
                      }
                      align="start"
                      locale="en-GB"
                      showCompare={false}
                      triggerClassName={filterTriggerClass(!!targetDateFrom)}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("table.filters.filterByDate")}</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={handleResetFilters}
                    className={cn("min-w-8 shrink-0", filterTriggerClass(false))}
                  >
                    <RefreshCcwIcon className="opacity-50" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("table.filters.resetFilters")}</p>
                </TooltipContent>
              </Tooltip>

              {endActions.map((action, index) => {
                const Icon = action.icon;
                if (action.render) {
                  return (
                    <Fragment key={`desktop-action-${index}`}>
                      {action.render()}
                    </Fragment>
                  );
                }
                return (
                  <Tooltip key={`desktop-action-${index}`}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={action.variant ?? "outline"}
                        onClick={action.onClick}
                        className="rounded-md"
                      >
                        {Icon && <Icon className="2xl:mr-1" />}
                        <span className="hidden 2xl:inline">{action.label}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{action.label}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}

              {showImportButton &&
                onImportClick &&
                (permissionsLoading ? (
                  <Skeleton className="h-8 w-9 2xl:w-24 rounded-md" />
                ) : can("create") ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="secondary"
                        onClick={onImportClick}
                        className="rounded-md"
                      >
                        <Upload className="2xl:mr-1" />
                        <span className="hidden 2xl:inline">
                          {t("table.filters.import")}
                        </span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t("table.filters.import")} {tableName}s
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : null)}

              {(setIsModalOpen || createRoute) &&
                (permissionsLoading ? (
                  <Skeleton
                    className={`h-8 rounded-md ${filters.length <= 2 ? "w-24" : "size-8"}`}
                  />
                ) : can("create") ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        onClick={handleNewItem}
                        className="rounded-md"
                      >
                        <Plus className="2xl:mr-1" />
                        {filters.length <= 2 && (
                          <span className="md:inline sm:inline lg:inline xl:inline 2xl:inline hidden">
                            {t("table.filters.add")}
                          </span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="flex items-center gap-2">
                      <p>
                        {t("table.filters.add")} {tableName}
                      </p>
                      {createShortcut && <TooltipKbd>{createShortcut}</TooltipKbd>}
                    </TooltipContent>
                  </Tooltip>
                ) : null)}
            </div>
            )}
        </div>
      </div>
      </div>
    </TooltipProvider>
  );
};
